import _ from 'lodash'
import { named, withDependencies } from '@wix/thunderbolt-ioc'
import { CurrentRouteInfoSymbol, EditorFeatureConfigSymbol } from '@wix/thunderbolt-symbols'
import { RootNavigationInfo } from 'thunderbolt-viewer-manager-types'
import { CandidateRouteInfo, ICurrentRouteInfo } from 'feature-router'
import { ILightboxUtils, LightboxUtilsSymbol } from 'feature-lightbox'
import type { INavigationInfo, NavigationInfoState, EditorNavigationConfig } from './types'
import { name } from './symbols'

const getPathParts = (relativeUrl: string): Array<string> => relativeUrl.replace('./', '').split('/')

const navigationInfo = (
	navigationConfig: EditorNavigationConfig,
	{ getCurrentRouteInfo, getWantedRouteInfo }: ICurrentRouteInfo,
	popups: ILightboxUtils
): INavigationInfo => {
	const state: NavigationInfoState = {
		rootInfo: {},
		nextRootInfo: {},
		loadedPageIds: {},
		loadedPageIdsByViewMode: { desktop: {}, mobile: {} },
		isDuringInitialNavigationPhase: false,
		isDuringFinalNavigationPhase: false,
	}

	const getPrimaryPageNavigationInfo = () => {
		const routeInfo = (getWantedRouteInfo() || getCurrentRouteInfo()) as CandidateRouteInfo
		if (routeInfo?.type === 'Dynamic') {
			const pathParts = getPathParts(routeInfo?.relativeEncodedUrl!)
			const [prefix, ...innerRoutePathParts] = pathParts
			const routerDefinition = navigationConfig.prefixToRoutersDefinition[prefix]
			const innerRoute = innerRoutePathParts.join('/')
			const pageAdditionalData = `${prefix}/${innerRoute}`

			return {
				routerDefinition: routerDefinition as RootNavigationInfo['routerDefinition'],
				innerRoute,
				pageAdditionalData,
			}
		}

		return {
			pageId: routeInfo?.pageId,
			relativeUrl: routeInfo?.relativeUrl,
		}
	}

	const getPopUpNavigationInfo = () => {
		const popupId = popups.getCurrentLightboxId()
		return popupId ? { pageId: popupId } : null
	}

	return {
		getNextRootNavigationInfo: () => state.nextRootInfo,
		getCurrentRootNavigationInfo: () => {
			if (navigationConfig.isPreview) {
				return getPopUpNavigationInfo() || getPrimaryPageNavigationInfo()
			}
			return state.rootInfo
		},
		getLoadedPageIds: () => state.loadedPageIds,
		getLoadedPageIdsByViewMode: (viewMode) => state.loadedPageIdsByViewMode[viewMode],
		updateCurrentRootNavigationInfo: (nextRootInfo: Partial<RootNavigationInfo>) => {
			// we can have multiple navigation calls where nextRootInfo is set multiple times before ending the first navigation
			// in this case we want to clear the nextRootInfo only when we finish all navigations
			const isLastNavigation = _.isEqual(state.nextRootInfo, nextRootInfo)
			if (isLastNavigation) {
				state.nextRootInfo = null
			}
			state.rootInfo = nextRootInfo
		},
		updateNextRootNavigationInfo: (nextRootInfo: Partial<RootNavigationInfo>) => {
			state.nextRootInfo = nextRootInfo
		},
		updateLoadedPageIds: (pageIds, viewMode) => {
			pageIds.forEach((pageId) => {
				state.loadedPageIds[pageId] = true
				state.loadedPageIdsByViewMode.desktop[pageId] = viewMode === 'desktop'
				state.loadedPageIdsByViewMode.mobile[pageId] = viewMode === 'mobile'
			})
		},
		startInitialNavigationPhase: () => {
			state.isDuringInitialNavigationPhase = true
		},
		endInitialNavigationPhase: () => {
			state.isDuringInitialNavigationPhase = false
		},
		isDuringInitialNavigationPhase: () => state.isDuringInitialNavigationPhase,
		startFinalNavigationPhase: () => {
			state.isDuringFinalNavigationPhase = true
		},
		endFinalNavigationPhase: () => {
			state.isDuringFinalNavigationPhase = false
		},
		isDuringFinalNavigationPhase: () => state.isDuringFinalNavigationPhase,
		getPrimaryPageNavigationInfo,
		getPopUpNavigationInfo,
		clearLoadedPages: () => {
			state.loadedPageIdsByViewMode.desktop = {}
			state.loadedPageIdsByViewMode.mobile = {}
		},
	}
}

export const NavigationInfo = withDependencies(
	[named(EditorFeatureConfigSymbol, name), CurrentRouteInfoSymbol, LightboxUtilsSymbol],
	navigationInfo
)
